/**
 * Helpers de formato y utilidades compartidas para el módulo Booking.
 */

export function getEmpresaId() {
  // Prioridad: empresa_activa (selector superadmin) > user.company_id (login normal)
  try {
    const empresaActiva = localStorage.getItem('empresa_activa');
    if (empresaActiva) return empresaActiva;
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return u.company_id || u.empresa_id || '1';
  } catch { return '1'; }
}

export function getUserRole() {
  try { return JSON.parse(localStorage.getItem('user') || '{}').role || 'miembro'; }
  catch { return 'miembro'; }
}

export function getAuthHeaders() {
  const t = localStorage.getItem('token');
  // X-User-Id es la identidad que realmente lee el backend (dependencies.
  // get_current_user). El token de Firebase caduca a la hora y los endpoints
  // que lo exigen responden 401 en mitad de la jornada; por eso el resto de
  // las pantallas —finanzas, vouchers, pagos recibidos— mandan este header.
  const uid = localStorage.getItem('user_id');
  return {
    'Content-Type': 'application/json',
    ...(t ? { Authorization: `Bearer ${t}` } : {}),
    ...(uid ? { 'X-User-Id': uid } : {}),
  };
}

export const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

export function todayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Determina si una reserva PUEDE borrarse dado el cutoff de la empresa.
 * Reglas (mismas que el backend en `puede_reservarse_para`):
 *   - Si salida es futura → true.
 *   - Si salida es hoy y hora local actual ≤ cutoff → true.
 *   - En cualquier otro caso → false (con motivo).
 *
 * @param {string} salidaFecha  "YYYY-MM-DD" o "YYYY-MM-DDTHH:MM:SS".
 * @param {string} horaCutoff  "HH:MM" (default "09:00" si no viene).
 * @returns {{ok: boolean, motivo: string}}
 */
export function puedeBorrarReserva(salidaFecha, horaCutoff = '09:00', estadoEmbarque = null) {
  // Una reserva embarcada ya viajó: no se borra ni el día de la salida, por
  // más que el cutoff todavía no se haya cumplido. El backend la rechaza
  // igual; esto es para que el botón lo diga antes de intentarlo.
  if (estadoEmbarque === 'embarcado') {
    return { ok: false, motivo: 'Ya está embarcada: sus pasajeros viajaron.' };
  }
  if (!salidaFecha) return { ok: false, motivo: 'Sin fecha de salida.' };
  const fechaSalida = String(salidaFecha).slice(0, 10);   // YYYY-MM-DD
  const hoy = todayLocal();
  if (fechaSalida < hoy) return { ok: false, motivo: 'La salida ya pasó.' };
  if (fechaSalida > hoy) return { ok: true, motivo: '' };
  // Es hoy — comparar contra cutoff
  const cutoff = (horaCutoff || '09:00').slice(0, 5);
  const now = new Date();
  const horaActual = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  if (horaActual <= cutoff) return { ok: true, motivo: '' };
  return { ok: false, motivo: `El cutoff de la salida (${cutoff}) ya se cumplió.` };
}

export function formatPrecio(val, moneda = 'COP') {
  const n = Number(val) || 0;
  return new Intl.NumberFormat('es-CO', {
    style: 'currency', currency: moneda || 'COP', minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(n);
}

export function formatFechaCorta(s) {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return s; }
}

export function formatFechaLarga(s) {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleDateString('es-CO', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });
  } catch { return s; }
}

export function formatHora(s) {
  if (!s) return '';
  return String(s).slice(0, 5);
}

export function fmtPax(r) {
  const parts = [];
  if (r.adultos) parts.push(`${r.adultos}A`);
  if (r.ninos) parts.push(`${r.ninos}N`);
  if (r.bebes) parts.push(`${r.bebes}B`);
  return parts.join(' · ') || '—';
}

const DIACRITICS = new RegExp('[\\u0300-\\u036f]', 'g');
/** Normaliza texto para búsqueda (sin acentos, lowercase). */
export function norm(s) {
  return String(s || '').normalize('NFD').replace(DIACRITICS, '').toLowerCase();
}
